import { useEffect, useState } from "react";
import { Link, Redirect, useParams } from "wouter";
import { useAuth } from "../hooks/useAuth";
import { customerFetch } from "../lib/customerFetch";
import { Wordmark } from "../components/Brand";

/**
 * Customer Card Detail — Phase 9.
 *
 * Route: /cards/:tcId
 * Shows balance, the merchant's reward catalog with `canRedeem` flags
 * (server-side computed, including OUT_OF_STOCK + INSUFFICIENT_BALANCE
 * unmet reasons), and the customer's recent activity (transactions ∪
 * redemptions, newest-first).
 *
 * Tapping a reward POSTs /customer/me/cards/:tcId/redeem and routes
 * to the issued voucher detail page.
 */

interface AvailableReward {
  id: string;
  name: string;
  description: string | null;
  metadata: { type: string; [k: string]: unknown };
  pointsCost: number | null;
  stampsCost: number | null;
  voucherTtlDays: number;
  stockRemaining: number | null;
  canRedeem: boolean;
  unmetReason: string | null;
}

interface ActivityItem {
  kind: "transaction" | "redemption";
  id: string;
  occurredAt: string;
  amount?: number | null;
  pointsAwarded?: number | null;
  stampsAwarded?: number | null;
  rewardName?: string | null;
  status?: string | null;
}

interface CardSummary {
  tenantCustomerId: string;
  tenantId: string;
  tenantName: string;
  loyaltyMode: "POINTS" | "STAMPS" | "VISIT_TIERS" | "HYBRID";
  pointsBalance: number;
  stampsCount: number;
  stampsRewardThreshold: number;
  totalVisits: number;
}

export function MerchantDetailPage() {
  const { tcId = "" } = useParams<{ tcId: string }>();
  const { isAuthenticated } = useAuth();

  const [card, setCard] = useState<CardSummary | null>(null);
  const [rewards, setRewards] = useState<AvailableReward[] | null>(null);
  const [activity, setActivity] = useState<ActivityItem[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [redeemingId, setRedeemingId] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  // D99 — leave-merchant action lives at the bottom of this page.
  // Uses the existing POST /customer/me/merchants/:tcId/revoke
  // endpoint (Phase 12). Local busy state so the button can show
  // "Leaving…" while the request is in flight; on success we full-
  // page-navigate back to /cards so the my-cards query refetches
  // cleanly with no stale Tanstack cache to invalidate.
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (!isAuthenticated || !tcId) return;
    let cancelled = false;
    void (async () => {
      setErr(null);
      // Card summary comes from /customer/me/cards (we filter to one).
      const cardsR = await customerFetch<{
        cards: CardSummary[];
      }>("/api/v1/customer/me/cards");
      if (cancelled) return;
      if (!cardsR.ok) {
        setErr(cardsR.message);
        return;
      }
      const found =
        cardsR.data.cards.find((c) => c.tenantCustomerId === tcId) ?? null;
      setCard(found);

      const rewardsR = await customerFetch<AvailableReward[]>(
        `/api/v1/customer/me/cards/${tcId}/rewards`,
      );
      if (cancelled) return;
      if (rewardsR.ok) setRewards(rewardsR.data);

      const actR = await customerFetch<ActivityItem[]>(
        `/api/v1/customer/me/cards/${tcId}/activity`,
      );
      if (cancelled) return;
      if (actR.ok) setActivity(actR.data);
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, tcId, refreshKey]);

  if (!isAuthenticated) return <Redirect to="/login" />;

  async function onRedeem(reward: AvailableReward) {
    const method: "points" | "stamps" =
      reward.pointsCost !== null ? "points" : "stamps";
    const ok = window.confirm(
      `Spend ${
        method === "points" ? `${reward.pointsCost!.toFixed(2)} points` : `${reward.stampsCost} stamps`
      } on "${reward.name}"?`,
    );
    if (!ok) return;
    setRedeemingId(reward.id);
    const r = await customerFetch<{ id: string }>(
      `/api/v1/customer/me/cards/${tcId}/redeem`,
      {
        method: "POST",
        body: JSON.stringify({
          rewardId: reward.id,
          paymentMethod: method,
        }),
      },
    );
    setRedeemingId(null);
    if (!r.ok) {
      window.alert(`${r.code}: ${r.message}`);
      return;
    }
    window.location.href = `/vouchers/${r.data.id}`;
  }

  return (
    <div className="min-h-screen pb-24">
      <div className="mx-auto max-w-md px-6 pt-8">
        <header className="flex items-center justify-between">
          <Wordmark size="sm" />
          <Link href="/cards" className="text-sm text-ink-dark hover:text-navy">
            ← Back
          </Link>
        </header>

        {err && (
          <div className="mt-4 rounded-card border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            {err}
          </div>
        )}

        {card === null ? (
          <p className="mt-6 text-sm text-ink-dark">Loading…</p>
        ) : (
          <>
            <section className="mt-6 card">
              <h1 className="text-xl font-bold text-navy">
                {card.tenantName}
              </h1>
              <p className="mt-1 text-3xl font-bold text-navy">
                {card.loyaltyMode === "STAMPS"
                  ? `${card.stampsCount} / ${card.stampsRewardThreshold}`
                  : `${card.pointsBalance.toFixed(2)}`}
                <span className="ml-2 text-sm font-medium uppercase tracking-wider text-ink-dark">
                  {card.loyaltyMode === "STAMPS" ? "stamps" : "points"}
                </span>
              </p>
            </section>

            <section className="mt-6">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-dark">
                Available rewards
              </h2>
              <div className="mt-3 space-y-2">
                {rewards === null ? (
                  <p className="text-sm text-ink-dark">Loading rewards…</p>
                ) : rewards.length === 0 ? (
                  <p className="text-sm text-ink-dark">
                    No rewards available yet.
                  </p>
                ) : (
                  rewards.map((r) => (
                    <button
                      key={r.id}
                      type="button"
                      disabled={!r.canRedeem || redeemingId === r.id}
                      onClick={() => void onRedeem(r)}
                      className={`w-full text-left card transition ${
                        r.canRedeem
                          ? "hover:border-navy/30 active:scale-[0.99]"
                          : "opacity-60 cursor-not-allowed"
                      }`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="font-semibold text-navy">{r.name}</p>
                          <p className="text-xs text-ink-dark">
                            {r.pointsCost !== null
                              ? `${r.pointsCost!.toFixed(2)} points`
                              : `${r.stampsCost} stamps`}
                            {" · TTL "}
                            {r.voucherTtlDays}d
                          </p>
                          {!r.canRedeem && r.unmetReason && (
                            <p className="mt-1 text-[11px] uppercase tracking-wider text-red-600">
                              {r.unmetReason}
                            </p>
                          )}
                        </div>
                        {r.canRedeem && (
                          <span className="text-sm font-semibold text-navy shrink-0">
                            {redeemingId === r.id ? "Redeeming…" : "Redeem →"}
                          </span>
                        )}
                      </div>
                    </button>
                  ))
                )}
              </div>
            </section>

            <section className="mt-8">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-dark">
                Recent activity
              </h2>
              <div className="mt-3 space-y-1">
                {activity === null ? (
                  <p className="text-sm text-ink-dark">Loading…</p>
                ) : activity.length === 0 ? (
                  <p className="text-sm text-ink-dark">Nothing here yet.</p>
                ) : (
                  activity.slice(0, 20).map((a) => (
                    <div
                      key={`${a.kind}-${a.id}`}
                      className="flex items-center justify-between border-b border-amber-light/60 py-2 text-sm"
                    >
                      <div className="min-w-0">
                        <p className="text-navy truncate">
                          {a.kind === "transaction"
                            ? `+${a.pointsAwarded ?? 0} pts${
                                a.stampsAwarded
                                  ? ` · +${a.stampsAwarded} stamps`
                                  : ""
                              }${a.amount != null ? ` (₱${a.amount})` : ""}`
                            : `Redeemed ${a.rewardName}`}
                        </p>
                        <p className="text-[11px] text-ink-dark">
                          {new Date(a.occurredAt).toLocaleString()}
                        </p>
                      </div>
                      {a.kind === "redemption" && a.status && (
                        <span className="ml-2 rounded-full bg-navy/5 px-2 py-0.5 text-[10px] uppercase tracking-wider text-navy shrink-0">
                          {a.status}
                        </span>
                      )}
                    </div>
                  ))
                )}
              </div>
              <button
                type="button"
                onClick={() => setRefreshKey((k) => k + 1)}
                className="mt-4 text-sm text-navy underline"
              >
                Refresh
              </button>
            </section>

            {/* D99 — leave-merchant action. Surfaces the existing
                Phase 12 revoke endpoint in a place customers will
                actually find it (Profile → Connected merchants is
                the other entry point and is much less discoverable
                from the Cards-tab flow). Status preserved server-
                side so re-joining via the merchant's Join QR
                restores points/stamps (D29). */}
            <section className="mt-10 border-t border-amber-light/60 pt-6">
              <h2 className="text-xs font-semibold uppercase tracking-wider text-ink-dark">
                Manage card
              </h2>
              <p className="mt-2 text-xs text-ink-dark">
                Leaving stops them from seeing your card or messaging
                you. Your points and stamps are kept — re-join later
                to restore them.
              </p>
              <button
                type="button"
                disabled={leaving}
                onClick={async () => {
                  const ok = window.confirm(
                    `Leave ${card.tenantName}? They'll no longer see your card. You can re-join anytime via their QR.`,
                  );
                  if (!ok) return;
                  setLeaving(true);
                  const r = await customerFetch<{ ok: true }>(
                    `/api/v1/customer/me/merchants/${tcId}/revoke`,
                    { method: "POST", body: JSON.stringify({}) },
                  );
                  if (!r.ok) {
                    setLeaving(false);
                    window.alert(`${r.code}: ${r.message}`);
                    return;
                  }
                  // Full reload so the cards list refetches with no
                  // stale cache. Mirrors the D98 navigation pattern.
                  window.location.assign("/cards");
                }}
                className="mt-3 w-full rounded-xl border border-red-300 bg-red-50 py-3 text-sm font-semibold text-red-700 hover:bg-red-100 disabled:opacity-60 active:scale-[0.99] transition"
                data-testid="button-leave-merchant"
              >
                {leaving ? "Leaving…" : `Leave ${card.tenantName}`}
              </button>
            </section>
          </>
        )}
      </div>
    </div>
  );
}
